import { type NotificationView, notificationPath } from "@procuremate/shared";
import { clsx } from "clsx";
import { Bell } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useMarkAllRead, useMarkRead, useNotifications } from "./api";

/**
 * The header bell (WP-3.4, ADR-0023): unread count, and a panel with the latest notifications.
 * Opening one marks it read. Escape or a click outside closes the panel; focus returns to the bell.
 */
export function NotificationBell({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const list = useNotifications(enabled);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const [open, setOpen] = useState(false);
  // Phones: the panel spans the screen just below the bell (the header's height varies there).
  const [phoneTop, setPhoneTop] = useState<number | undefined>(undefined);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const titleId = useId();
  const unread = list.data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !button.current?.contains(target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]);

  function close() {
    setOpen(false);
    button.current?.focus();
  }

  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={unread > 0 ? t("notifications.bellUnread", { count: unread }) : t("notifications.bell")}
        onClick={() => {
          const phone = window.matchMedia("(max-width: 639px)").matches;
          setPhoneTop(phone && button.current ? button.current.getBoundingClientRect().bottom + 8 : undefined);
          setOpen((o) => !o);
        }}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-sm hover:bg-primary-hover"
      >
        <Bell aria-hidden="true" className="h-5 w-5" />
        {unread > 0 ? (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 min-w-5 rounded-full bg-on-primary px-1 text-center text-xs leading-5 font-bold text-primary tabular"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          ref={panel}
          id={panelId}
          role="dialog"
          aria-labelledby={titleId}
          tabIndex={-1}
          style={phoneTop === undefined ? undefined : { top: phoneTop }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              close();
            }
          }}
          className="fixed inset-x-2 top-14 z-50 max-h-[70dvh] overflow-y-auto rounded-lg bg-surface text-fg shadow-card ring-1 ring-border sm:absolute sm:inset-x-auto sm:top-11 sm:right-0 sm:w-96"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h2 id={titleId} className="font-heading text-base font-semibold">
              {t("notifications.title")}
            </h2>
            {unread > 0 ? (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="rounded-sm px-2 py-1 text-sm font-semibold text-link hover:bg-bg disabled:opacity-60"
              >
                {t("notifications.markAll")}
              </button>
            ) : null}
          </div>
          {list.isError ? (
            <p role="alert" className="px-4 py-3 text-sm text-danger">
              {t("notifications.loadError")}
            </p>
          ) : list.data && list.data.items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-fg-muted">{t("notifications.empty")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {list.data?.items.map((n) => (
                <Item
                  key={n.id}
                  notification={n}
                  onOpen={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                    setOpen(false);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Item({ notification: n, onOpen }: { notification: NotificationView; onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(n.createdAt),
  );
  const unread = n.readAt === null;
  return (
    <li>
      <Link
        to={notificationPath(n.kind, n.request.id)}
        onClick={onOpen}
        className={clsx("flex gap-2 px-4 py-3 text-sm hover:bg-bg", unread ? "text-fg" : "text-fg-muted")}
      >
        <span
          aria-hidden="true"
          className={clsx("mt-1.5 h-2 w-2 shrink-0 rounded-full", unread ? "bg-primary" : "bg-transparent")}
        />
        <span className="min-w-0">
          {unread ? <span className="sr-only">{t("notifications.unread")} </span> : null}
          <span className={clsx("block break-words", unread && "font-semibold")}>
            {t(`notifications.kinds.${n.kind}`, {
              reference: n.request.reference,
              actor: n.actor ?? t("review.events.someone"),
            })}
          </span>
          {n.request.title ? <span className="block break-words text-fg-muted">{n.request.title}</span> : null}
          <span className="block text-xs text-fg-muted">{when}</span>
        </span>
      </Link>
    </li>
  );
}
